import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DataResult, DataStateChangeEventArgs, PredicateModel, Sorts } from '@syncfusion/ej2-angular-grids';
import { Predicate } from '@syncfusion/ej2-data';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ICustomer, ISelectAll, IServerSettings, IServiceError, ISubmission, IValidation } from '../models/index';
import { AuthenticationService } from './authentication.service';
import { SettingsService } from './settings.service';
import { StorageManagerService } from './storage-manager.service';
import { AppGlobals } from '../globals/appGlobals';
import { RegistrationTypeEnum } from '../enums/index';

@Injectable()
export class DeviceinventoryService {

  private action: string;
  private apiGatewayUrl: string;
  private commonApiGatewayUrl: string;
  private data: any;
  private settings: IServerSettings;
  private industryId = 1;
  private termCategoryId = 33;
  private termUsageId = 2;
  permissionsConst: any;


  apiResult: any;

  manageDiDownloadLogBS: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  submissionHistoryDownloadLogBS: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor (private http: HttpClient, private settingsService: SettingsService, private authService: AuthenticationService, private storage: StorageManagerService, private appGlobals: AppGlobals) {
  }

  public getAppGridDefaults() {
    return this.http.get('../assets/griddefaults.json');
  }

  public getDeviceMapping() {
    return this.http.get('../assets/device-mapping.json');
  }

  public getMockManageDI() {
    return this.http.get('../assets/mock/db_manage_di.json');
  }

  public getMockSubmissionHistory() {
    return this.http.get('../assets/mock/db_subm_history.json');
  }

  public getMockManageSubmissionItems() {
    return this.http.get('../assets/mock/db_manage_subm_items.json');
  }

  public getUserPrefsMock() {
    return this.http.get('../assets/mock/user-prefs.json');
  }

  public getSubmissionHistoryItemMock() {
    return this.http.get('../assets/mock/submission-item.json');
  }

  public getSubmissionItemMock() {
    return this.http.get('../assets/mock/submission-item-mock.json');
  }

  public getManageSubmissionItemMock() {
    return this.http.get('../assets/mock/manage_submission_Item_mock.json');
  }

  public getItemDetailsMock() {
    return this.http.get('../assets/mock/item_details_mock.json');
  }

  public getInventoryItem(documentId: string, clientId: number) {

    this.settings = this.settingsService.getSettings();
    this.apiGatewayUrl = this.settings.ApiGatewayUrl;
    this.action = `dos/v1/documents/${ documentId }?clientid=${ clientId }`;

    const headers = this.authService.setHeader();

    return this.http.get<any>(`${ this.apiGatewayUrl }/${ this.action }`, { headers: headers }).pipe(
      catchError(err => this.handleHttpError(err))
    );
  }

  public getSubmissionItemDetails(itemType: string, submissionGuid: string) {

    this.settings = this.settingsService.getSettings();
    this.apiGatewayUrl = this.settings.ApiGatewayUrl;
    this.action = `rpts/v1/aggregate/SubmissionItemDetailsByGuid?submissionGuid=${ submissionGuid }&itemType=${ itemType }`;

    const headers = this.authService.setHeader();

    return this.http.get<any>(`${ this.apiGatewayUrl }/${ this.action }`, { headers: headers }).pipe(
      catchError(err => this.handleHttpError(err))
    );
  }

  public getDataScopes() {
    this.settings = this.settingsService.getSettings();
    this.commonApiGatewayUrl = this.settings.CommonApiGatewayUrl;
    this.action = `rds/v1/terms/industry?industryId=${ this.industryId }&termusageId=${ this.termUsageId }&categoryId=${ this.termCategoryId }`;

    const headers = this.authService.setHeader();

    return this.http.get<any>(`${ this.commonApiGatewayUrl }/${ this.action }`, { headers: headers }).pipe(
      catchError(err => this.handleHttpError(err))
    );
  }

  public getUserPrefs(userGuid: string, customerId: string) {

    this.permissionsConst = this.appGlobals.permissions;
    this.settings = this.settingsService.getSettings();
    this.commonApiGatewayUrl = this.settings.CommonApiGatewayUrl;
    this.action = `set/v1/Users`;

    let headers = this.authService.setHeader();
    headers = headers.set('userId', userGuid);
    headers = headers.set('customerId', customerId);
    headers = headers.set('permission', this.permissionsConst.CanReadData);

    return this.http.get<any>(`${ this.commonApiGatewayUrl }/${ this.action }/${ userGuid }?applicationId=SingleSource`, { headers: headers }).pipe(
      catchError(err => this.handleHttpError(err))
    );

  }

  saveUserPrefs(userPrefs: any) {

    this.settings = this.settingsService.getSettings();
    this.commonApiGatewayUrl = this.settings.CommonApiGatewayUrl;
    this.action = `set/v1/Users`;

    const headers = this.authService.setHeader();

    return this.http.post(`${ this.commonApiGatewayUrl }/${ this.action }`, userPrefs, { headers: headers }).pipe(
      catchError(err => this.handleHttpError(err))
    );
  }

  public execute(state: any, grid: any, clientId: number, itemType: string, dataScope: string, language: string) {
    this.getData(state, grid, clientId, itemType, dataScope, language).subscribe(x => {
    });
  }


  getTimeZone() {

    let offset = new Date().getTimezoneOffset(), o = Math.abs(offset);
    return (offset < 0 ? "+" : "-") + ("00" + Math.floor(o / 60)).slice(-2) + ":" + ("00" + (o % 60)).slice(-2);
  }

  public getData(state: DataStateChangeEventArgs, grid, clientId: number, itemType: string, dataScope: string, language: string, 
                 registrationType: RegistrationTypeEnum = undefined): Observable<DataStateChangeEventArgs> {
    const pageQuery = `skip=${ state.skip }&top=${ state.take }`;
    let sortQuery = '&orderby=Modified Date/Time desc';
    let filterQuery = '';

    if ((state.sorted || []).length) {
      sortQuery = `&orderby=` + state.sorted.map((obj: Sorts) => {
        return obj.direction === 'descending' ? `${ obj.name } desc` : `${ obj.name } asc`;
      }).reverse().join(',');
    }

    if (state.where) {
      filterQuery = `&filter=` + state.where.map((obj: any) => {
        return (<Predicate>obj).predicates.map((predicate) => {
          if ((<any>predicate).predicates) {
            if (<any>grid.getColumnByField((<any>predicate).predicates[0].field).type === 'date') {
              return `((${ (<any>predicate).predicates[0].field } gt ${ (<any>predicate).predicates[0].value }) and (${ (<any>predicate).predicates[1].field } lt ${ (<any>predicate).predicates[1].value }))`;
            }
          } else {
            return predicate.operator === 'equal' ? `${ predicate.field } eq ${ predicate.value }` : `${ predicate.operator }(tolower(${ predicate.field }),'${ predicate.value }')`;
          }
        }).reverse().join(' and ');
      });
    }

    this.action = 'rpts/v1/Aggregate/ItemInventory';
    this.settings = this.settingsService.getSettings();
    this.apiGatewayUrl = this.settings.ApiGatewayUrl;

    let headers = this.authService.setHeader();

    headers = headers.set('clientId', clientId.toString());
    headers = headers.set('itemType', itemType);
    headers = headers.set('language', language);
    headers = headers.set('dataScope', dataScope.toLowerCase());

    filterQuery = `${ filterQuery }&timeZoneOffset=${ this.getTimeZone() }`

    this.storage.write('selectAllFilter', filterQuery);

    let apiUrl = `${ this.apiGatewayUrl }/${ this.action }?${ pageQuery }${ sortQuery }${ filterQuery }`;
    // We only want to pass the RegistrationType argument for Legacy requests.
    if(registrationType === RegistrationTypeEnum.Legacy){
      apiUrl += `&registrationType=${registrationType}`;
    }

    return this.http.get(apiUrl, { headers: headers }).pipe(
      map(
        this.extractData
      ),
      catchError(err => this.handleHttpError(err))
    );
  }

  public validateSelectedDevices(validations: IValidation[], clientId: number, priorityId: number) {

    this.settings = this.settingsService.getSettings();
    this.apiGatewayUrl = this.settings.ApiGatewayUrl;
    this.action = `val/v1/Validation/CreateValidation?priorityId=${priorityId}`;

    const authData = this.getAuthData();

    let headers = this.authService.setHeader();

    headers = headers.set('userId', authData['userGuid']);
    headers = headers.set('customerId', clientId.toString());

    return this.http.post(`${ this.apiGatewayUrl }/${ this.action }`, validations, { headers: headers }).pipe(
      catchError(err => this.handleHttpError(err))
    );
  }

  public approveSelectedDevices(submissions: ISubmission[], clientId: number) {

    this.settings = this.settingsService.getSettings();
    this.apiGatewayUrl = this.settings.ApiGatewayUrl;
    this.action = `sub/v1/Submission/CreateSubmission`;

    const headers = this.authService.setHeader();

    return this.http.post(`${ this.apiGatewayUrl }/${ this.action }`, submissions, { headers: headers }).pipe(
      catchError(err => this.handleHttpError(err))
    );
  }

  public getRevisionHistory(documentId: string, clientId: number) {

    this.settings = this.settingsService.getSettings();
    this.apiGatewayUrl = this.settings.ApiGatewayUrl;
    this.action = `dos/v1/DocumentRevisionHistory`;

    const headers = this.authService.setHeader();

    return this.http.get<any>(`${ this.apiGatewayUrl }/${ this.action }/${ documentId }/revisionhistory?clientId=${ clientId }`, { headers: headers }).pipe(
      catchError(err => this.handleHttpError(err))
    );
  }

  public validateSelectedAll(itemType: string, dataScope: string, filter: string, registrationType: number = 1) {

    this.settings = this.settingsService.getSettings();
    this.apiGatewayUrl = this.settings.ApiGatewayUrl;
    this.action = `rpts/v1/DataWarehouse/ValidateByFilter`;

    let headers = this.authService.setHeader();

    headers = headers.set('itemType', itemType);
    headers = headers.set('dataScope', dataScope);

    // If the filter is present, we need to lead with a & else a ? (since RegistrationType will be the only query)
    let regTypeQueryString = filter && filter.length > 0 ? '&' : '?'
    regTypeQueryString += `registrationType=${registrationType}`;
    const url = `${ this.apiGatewayUrl }/${ this.action }${ filter ?? '' }${regTypeQueryString}`;

    return this.http.get(url, { headers: headers }).pipe(
      catchError(err => this.handleHttpError(err))
    );
  }

  public submitSelectedAll(itemType: string, dataScope: string, filter: string, registrationTypeCode: number = 1) {

    this.settings = this.settingsService.getSettings();
    this.apiGatewayUrl = this.settings.ApiGatewayUrl;
    this.action = `rpts/v1/DataWarehouse/SubmitByFilter`;

    let headers = this.authService.setHeader();

    headers = headers.set('itemType', itemType);
    headers = headers.set('dataScope', dataScope);

    // If the filter is present, we need to lead with a & else a ? (since RegistrationType will be the only query)
    let regTypeCodeQueryString = filter && filter.length > 0 ? '&' : '?'
    regTypeCodeQueryString += `registrationTypeCode=${registrationTypeCode}`;

    const url = `${this.apiGatewayUrl}/${this.action}${filter ?? ''}${regTypeCodeQueryString}`;

    return this.http.get(url, { headers: headers }).pipe(
      catchError(err => this.handleHttpError(err))
    );
  }

  public downloadValidationLogAll(itemType: string, dataScope: string, registrationType: number = 1, filter: string) {

    this.settings = this.settingsService.getSettings();
    this.apiGatewayUrl = this.settings.ApiGatewayUrl;
    this.action = `rpts/v1/ExtractLog/CreateValidationLogTask`;

    let headers = this.authService.setHeader(false, true);

    headers = headers.set('itemType', itemType);
    headers = headers.set('applicationVariantName', dataScope);
    headers = headers.set('registrationType', registrationType.toString());

    let validationLogQuery = filter ?? '';
    if (validationLogQuery.indexOf('timeZoneOffset=') === -1) {
      validationLogQuery += `${ validationLogQuery.length > 0 ? '&' : '?' }timeZoneOffset=${ this.getTimeZone() }`;
    }

    const url = `${ this.apiGatewayUrl }/${ this.action }${ validationLogQuery }`;

    return this.http.get(url, { headers: headers }).pipe(
      catchError(err => this.handleHttpError(err))
    );
  }

  public downloadSubmissionLogAll(dataScope: string, filter: ISelectAll, all: boolean) {

    this.settings = this.settingsService.getSettings();
    this.apiGatewayUrl = this.settings.ApiGatewayUrl;
    this.action = `rpts/v1/ExtractLog/CreateSubmissionLogTask`;

    let headers = this.authService.setHeader(false, true);

    headers = headers.set('applicationVariantName', dataScope);

    const url = `${ this.apiGatewayUrl }/${ this.action }?timeZoneOffset=${ this.getTimeZone() }&all=${all}`;

    return this.http.post(`${ url }`, filter, { headers: headers }).pipe(
      catchError(err => this.handleHttpError(err))
    );
  }


  public getDownloadLogStatus(guid: string) {

    this.settings = this.settingsService.getSettings();
    this.apiGatewayUrl = this.settings.ApiGatewayUrl;
    this.action = `rpts/v1/ExtractLog/CheckLogTask?taskguid=${ guid }`;

    let headers = this.authService.setHeader();

    const url = `${ this.apiGatewayUrl }/${ this.action }`;

    return this.http.get(url, { headers: headers }).pipe(
      catchError(err => this.handleHttpError(err))
    );
  }


  getValidationLog(paramsContainer: any) {

    this.settings = this.settingsService.getSettings();
    this.apiGatewayUrl = this.settings.ApiGatewayUrl;
    this.action = `val/v1/Validation/ValidationLog`;

    const headers = this.authService.setHeader();

    return this.http.post(`${ this.apiGatewayUrl }/${ this.action }`, paramsContainer, { responseType: 'blob' as 'json', headers: headers }).pipe(
      map(response => response),
      catchError(err => this.handleHttpError(err))
    );
  }

  getSubmissionLog(paramsContainer: any) {

    this.settings = this.settingsService.getSettings();
    this.apiGatewayUrl = this.settings.ApiGatewayUrl;
    this.action = `sub/v1/Submission/SubmissionLog`;

    const headers = this.authService.setHeader();

    return this.http.post(`${ this.apiGatewayUrl }/${ this.action }`, paramsContainer, { responseType: 'blob' as 'json', headers: headers }).pipe(
      map(response => response),
      catchError(err => this.handleHttpError(err))
    );
  }

  private extractData(response: Response) {

    this.data = <DataResult>{
      result: response['value']['data'].result,
      count: +response['value']['data'].count
    };

    return this.data || {};
  }

  private getAuthData(): any {

    let applications: [] = [];
    let appVariantId: string;
    let authData = {};

    authData = this.storage.read('userData');

    const cust = this.storage.read('currentCustomer');

    const customer = typeof cust === 'object' ? cust : JSON.parse(cust);

    if (authData) {

      const userGuid = authData['userguid'];

      if (authData['applications']) {
        applications = JSON.parse(authData['applications']);

        if (applications.length > 0) {
          appVariantId = applications.find(a => a['AName'] === 'SingleSource')['AVId'];

          if (userGuid && appVariantId) {

            authData['userGuid'] = userGuid;
            authData['customerId'] = customer.customerId.toString();
            authData['applicationVariantId'] = appVariantId.toString();
            authData['customerGuid'] = customer.customerGuid;

          }
        }
      }
    }

    return authData;
  }

  getSubmissionHistory(clientId, channel, state: DataStateChangeEventArgs, grid, all: boolean): Observable<DataStateChangeEventArgs> {

    this.settings = this.settingsService.getSettings();
    this.apiGatewayUrl = this.settings.ApiGatewayUrl;
    this.action = `sub/v1/SubmissionHistory`;

    const params = `&customerId=${ clientId }&channel=${ channel }&all=${ all }`;

    const apiUrl = this.setApiCall(state, this.action, grid, params);

    const headers = this.authService.setHeader();

    return this.http.get(apiUrl, { headers: headers });
  }
  public getSubmissionHistoryData(state: DataStateChangeEventArgs, grid, clientId: number, dataScope: string, all: boolean): Observable<DataStateChangeEventArgs> {
    const pageQuery = `skip=${state.skip}&top=${state.take}&all=${all}`;    
    let sortQuery = '&orderby=createDate desc';
    switch (dataScope) {
      case 'china-udi':
        sortQuery = '&orderby=changeDate desc';
        break;
      case 'southkorea-udi':
        sortQuery = '&orderby=changeDate desc';
        break;
    }
    let filterQuery = '';

    if ((state.sorted || []).length) {
      sortQuery = `&orderby=` + state.sorted.map((obj: Sorts) => {
        return obj.direction === 'descending' ? `${obj.name} desc` : `${obj.name} asc`;
      }).reverse().join(',');
    }

    if (state.where) {
      filterQuery = `&filter=` + state.where.map((obj: any) => {
        return (<Predicate>obj).predicates.map((predicate) => {
          if ((<any>predicate).predicates) {
            if (<any>grid.getColumnByField((<any>predicate).predicates[0].field).type === 'date') {
              return `((${(<any>predicate).predicates[0].field} gt ${(<any>predicate).predicates[0].value}) and (${(<any>predicate).predicates[1].field} lt ${(<any>predicate).predicates[1].value}))`;
            }
          } else {
            return predicate.operator === 'equal' ? `${predicate.field} eq ${predicate.value}` : `${predicate.operator}(tolower(${predicate.field}),'${predicate.value}')`;
          }
        }).reverse().join(' and ');
      });
    }

    this.action = 'rpts/v1/Aggregate/SubmissionHistoryInventory';
    this.settings = this.settingsService.getSettings();
    this.apiGatewayUrl = this.settings.ApiGatewayUrl;

    let headers = this.authService.setHeader();

    headers = headers.set('clientId', clientId.toString());
    headers = headers.set('dataScope', dataScope.toLowerCase());

    filterQuery = `${filterQuery}&timeZoneOffset=${this.getTimeZone()}`

    this.storage.write('selectAllFilter', filterQuery);

    const apiUrl = `${this.apiGatewayUrl}/${this.action}?${pageQuery}${sortQuery}${filterQuery}`;

    return this.http.get(apiUrl, { headers: headers }).pipe(
      map(
        this.extractData
      ),
      catchError(err => this.handleHttpError(err))
    );
  }
  getSubmissionItems(status, type, clientId, channel, state: DataStateChangeEventArgs, grid): Observable<DataStateChangeEventArgs> {

    this.settings = this.settingsService.getSettings();
    this.apiGatewayUrl = this.settings.ApiGatewayUrl;
    this.action = `rpts/v1/Aggregate/GetSubmissions`;

    const params = `&customerId=${ clientId }&submissionStatus=${ status }&submissionType=${ type }&channel=${ channel }`;

    const apiUrl = this.setApiCall(state, this.action, grid, params);

    const headers = this.authService.setHeader();

    return this.http.get(apiUrl, { headers: headers });
  }

  setApiCall(state, action, grid, params): string {

    const pageQuery = `skip=${ state.skip }&top=${ state.take }`;
    let sortQuery = '';
    let filterQuery = '';

    if ((state.sorted || []).length) {
      sortQuery = `&orderby=` + state.sorted.map((obj: Sorts) => {
        return obj.direction === 'descending' ? `${ obj.name } desc` : `${ obj.name } asc`;
      }).reverse().join(',');
    }

    if (state.where) {
      filterQuery = `&filter=` + state.where.map((obj: PredicateModel) => {
        return (<Predicate>obj).predicates.map((predicate) => {
          if ((<any>predicate).predicates) {
            if (<any>grid.getColumnByField((<any>predicate).predicates[0].field).type === 'date') {
              return `((${ (<any>predicate).predicates[0].field } gt ${ (<any>predicate).predicates[0].value }) and (${ (<any>predicate).predicates[1].field } lt ${ (<any>predicate).predicates[1].value }))`;
            }
          } else {
            return predicate.operator === 'equal' ? `${ predicate.field } eq ${ predicate.value }` : `${ predicate.operator }(tolower(${ predicate.field }),'${ predicate.value }')`;
          }
        }).reverse().join(' and ');
      });
    }

    filterQuery = `${ filterQuery }&timeZoneOffset=${ this.getTimeZone() }`

    const apiUrl = `${ this.apiGatewayUrl }/${ action }?${ pageQuery }${ sortQuery }${ filterQuery }${ params }`;

    return apiUrl;
  }

  eSigConfirmation(paramsContainer: any) {

    this.settings = this.settingsService.getSettings();
    this.commonApiGatewayUrl = this.settings.CommonApiGatewayUrl;
    this.action = `ids/v1/User/ESignature`;

    const headers = this.authService.setHeader();

    return this.http.post(`${ this.commonApiGatewayUrl }/${ this.action }`, paramsContainer, { headers: headers }).pipe(
      catchError(err => this.handleHttpError(err))
    );
  }

  sendToChannelForRegistration(approveSubmissionViewModel: any) {

    this.settings = this.settingsService.getSettings();
    this.apiGatewayUrl = this.settings.ApiGatewayUrl;
    this.action = `sub/v1/Submission/ApproveSubmission`;

    const headers = this.authService.setHeader();

    return this.http.post(`${ this.apiGatewayUrl }/${ this.action }`, approveSubmissionViewModel, { headers: headers }).pipe(
      catchError(err => this.handleHttpError(err))
    );
  }

  sendToChannelSelectAll(viewModel: any) {

    this.settings = this.settingsService.getSettings();
    this.apiGatewayUrl = this.settings.ApiGatewayUrl;
    this.action = `rpts/v1/DataWarehouse/ApproveSubmissionBulk`;

    const headers = this.authService.setHeader();

    return this.http.post(`${ this.apiGatewayUrl }/${ this.action }?timeZoneOffset=${ this.getTimeZone() }`, viewModel, { headers: headers }).pipe(
      catchError(err => this.handleHttpError(err))
    );
  }

  sendForRejection(rejectSubmissionViewModel: any) {

    this.settings = this.settingsService.getSettings();
    this.apiGatewayUrl = this.settings.ApiGatewayUrl;
    this.action = `sub/v1/Submission/RejectSubmission`;

    const headers = this.authService.setHeader();

    return this.http.post(`${ this.apiGatewayUrl }/${ this.action }`, rejectSubmissionViewModel, { headers: headers }).pipe(
      catchError(err => this.handleHttpError(err))
    );
  }

  sendForRejectionSelectAll(rejectSubmissionViewModel: any) {

    this.settings = this.settingsService.getSettings();
    this.apiGatewayUrl = this.settings.ApiGatewayUrl;
    this.action = `rpts/v1/DataWarehouse/RejectSubmissionBulk`;

    const headers = this.authService.setHeader();

    return this.http.post(`${ this.apiGatewayUrl }/${ this.action }?timeZoneOffset=${ this.getTimeZone() }`, rejectSubmissionViewModel, { headers: headers }).pipe(
      catchError(err => this.handleHttpError(err))
    );
  }


  public getValidationStatusMultiple(paramsContainer) {

    this.settings = this.settingsService.getSettings();
    this.apiGatewayUrl = this.settings.ApiGatewayUrl;
    this.action = `rpts/v1/DataWarehouse/GetValidationStatusMultiple`;

    let headers = this.authService.setHeader();

    return this.http.post<any>(`${ this.apiGatewayUrl }/${ this.action }`, paramsContainer, { headers: headers }).pipe(
      catchError(err => this.handleHttpError(err))
    );
  }
  public getFeatureFlagStatus(flagName: string) {
    let url = `/FeatureFlag?name=${flagName}`;
    let headers = this.authService.setHeader();

    return this.http.get<any>(url, { headers: headers });
  }
  private handleHttpError(error: HttpErrorResponse): Observable<IServiceError> {

    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      const dataError = new IServiceError();
      dataError.errorNumber = error.error ? error.error.number : '';
      dataError.message = error.message;
      dataError.friendlyMessage = error.status ? `Http call failed with status ${error.status}` : `Http call failed`;
      dataError.errorMessage = error.error ? error.error.message : '';
      dataError.errorStatus = error.status;
      dataError.url = error.url;
      // return an observable of an IServiceError object with a user friendly error message
      return of(dataError);
    } 
  }
}

